import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { SportsProductService } from '@frontend/sports/host-app/sports-product/feature/sports-product-service';
import { filterSportsEmitLast, fromSportsLeave } from '@frontend/sports/host-app/sports-product/feature/utils';
import { BottomNavService, OnAppInit } from '@frontend/vanilla/core';
import { BetslipBarVisibilityService } from 'packages/sports/common/betslip/modules/betslip-bar/services/betslip-bar-visibility.service';
import { combineLatest, distinctUntilKeyChanged, switchMap, withLatestFrom } from 'rxjs';

import { DispatcherEvent } from '../event-details-common/event-details-common.models';
import { AdaptiveLayoutService } from '../layout/adaptive-layout.service';

@Injectable({ providedIn: 'root' })
export class ToggleBottomNavService implements OnAppInit {
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private adaptiveLayout: AdaptiveLayoutService,
        private bottomNavService: BottomNavService,
        private betslipBarVisibility: BetslipBarVisibilityService,
        private dispatcher: DispatcherService,
        private sportsProductService: SportsProductService,
    ) {}

    onAppInit(): void {
        const layoutBottomNavVisibility = this.adaptiveLayout.stateChange$.pipe(distinctUntilKeyChanged('bottomNavigation'));
        const vanillaNav = this.bottomNavService.whenReady.pipe(switchMap(() => this.bottomNavService.isEnabled()));

        // Reset bottom nav to show after we leave sports, only after bottom nav is triggered
        combineLatest([fromSportsLeave(this.sportsProductService), this.bottomNavService.whenReady])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.bottomNavService.show();
            });

        combineLatest([vanillaNav, layoutBottomNavVisibility])
            .pipe(filterSportsEmitLast(this.sportsProductService), takeUntilDestroyed(this.destroyRef))
            .subscribe(([vanillaNavEnabled, state]) => {
                if (!vanillaNavEnabled) {
                    return;
                }

                if (state.bottomNavigation) {
                    this.bottomNavService.show();
                    this.betslipBarVisibility.showComponent();
                } else {
                    this.bottomNavService.hide();
                    this.betslipBarVisibility.hideComponent();
                }
            });

        this.dispatcher
            .on<boolean>(DispatcherEvent.EventDetails.Expanded)
            .pipe(withLatestFrom(vanillaNav, layoutBottomNavVisibility))
            .subscribe(([expanded, enabled, state]) => {
                if (!enabled || !state.bottomNavigation) {
                    return;
                }

                if (expanded) {
                    this.bottomNavService.hide();
                    this.betslipBarVisibility.hideComponent();
                } else {
                    this.bottomNavService.show();
                    this.betslipBarVisibility.showComponent();
                }
            });
    }
}
